import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { gql } from "graphql-request";
import { Link } from "react-router-dom";
import { graphqlClient } from "@/lib/graphql";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { Pagination } from "@/components/ui/Pagination";
import { GameMergeModal } from "@/components/admin/GameMergeModal";
import type { AdminGameDetail } from "@/types";

// ── GraphQL ──────────────────────────────────────────

const ADMIN_MERGE_SUGGESTIONS_QUERY = gql`
  query AdminMergeSuggestions($page: Int, $perPage: Int) {
    adminMergeSuggestions(page: $page, perPage: $perPage) {
      games {
        id
        title
        yearPublished
        dataSource
        imageUrl
        primaryImageUrl
        activeListingsCount
        createdAt
        suggestions {
          id
          title
          yearPublished
          thumbnailUrl
          bggId
          imageSimilarity
          combinedScore
          isBggMatch
          activeListingsCount
          primaryImageUrl
        }
      }
      totalCount
      totalPages
      currentPage
    }
  }
`;

const ADMIN_GAME_QUERY = gql`
  query AdminGame($id: ID!) {
    adminGame(id: $id) {
      id title searchableTitle yearPublished dataSource
      bggId bggNotFound bggLastSyncedAt bggRating bggRank bayesAverage usersRated
      localRating localVotesCount displayRating ratingSource
      complexity minPlayers maxPlayers minPlaytime maxPlaytime minAge
      languageIndependent isExpansion imageUrl thumbnailUrl
      descriptionEn descriptionLt designer publisher
      designersList publishersList mechanics genres categories families expansions artists
      boxLength boxWidth boxHeight approvalStatus public
      alternateNames activeListingsCount
      shopListings { id shopName listingUrl price priceUpdatedAt updatedAt }
      createdAt updatedAt
    }
  }
`;

const UPDATE_GAME_APPROVAL_MUTATION = gql`
  mutation UpdateGameApproval($id: ID!, $approvalStatus: String!) {
    updateGameApproval(id: $id, approvalStatus: $approvalStatus) {
      game { id approvalStatus }
      errors
    }
  }
`;

const UPDATE_GAME_MUTATION = gql`
  mutation UpdateGame($id: ID!, $title: String) {
    updateGame(id: $id, title: $title) {
      game { id title }
      errors
    }
  }
`;

// ── Types ────────────────────────────────────────────

interface MergeSuggestion {
  id: string;
  title: string;
  yearPublished: number | null;
  thumbnailUrl: string | null;
  bggId: number | null;
  imageSimilarity: number | null;
  combinedScore: number;
  isBggMatch: boolean;
  activeListingsCount: number | null;
  primaryImageUrl: string | null;
}

interface MergeCandidate {
  id: string;
  title: string;
  yearPublished: number | null;
  dataSource: string | null;
  imageUrl: string | null;
  primaryImageUrl: string | null;
  activeListingsCount: number;
  createdAt: string;
  suggestions: MergeSuggestion[];
}

interface MergeSuggestionsResult {
  games: MergeCandidate[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
}

// ── Component ───────────────────────────────────────

export function AdminMergeGamesPage() {
  const [page, setPage] = useState(1);
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null);
  const [initialTargetId, setInitialTargetId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["adminMergeSuggestions", page],
    queryFn: () =>
      graphqlClient.request<{ adminMergeSuggestions: MergeSuggestionsResult }>(
        ADMIN_MERGE_SUGGESTIONS_QUERY,
        { page, perPage: 10 },
      ),
  });

  // Fetch full game for the merge modal
  const { data: sourceGameData } = useQuery({
    queryKey: ["adminGame", selectedSourceId],
    queryFn: () =>
      graphqlClient.request<{ adminGame: AdminGameDetail }>(ADMIN_GAME_QUERY, {
        id: selectedSourceId,
      }),
    enabled: !!selectedSourceId && mergeModalOpen,
  });

  const approveMutation = useMutation({
    mutationFn: (gameId: string) =>
      graphqlClient.request(UPDATE_GAME_APPROVAL_MUTATION, {
        id: gameId,
        approvalStatus: "approved",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminMergeSuggestions"] });
    },
  });

  const updateGameMutation = useMutation({
    mutationFn: (params: { id: string; title: string }) =>
      graphqlClient.request<{
        updateGame: { game: { id: string; title: string } | null; errors: string[] };
      }>(UPDATE_GAME_MUTATION, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminMergeSuggestions"] });
    },
  });

  const result = data?.adminMergeSuggestions;
  const sourceGame = sourceGameData?.adminGame;

  const openMergeModal = (gameId: string, targetId?: string) => {
    setSelectedSourceId(gameId);
    setInitialTargetId(targetId ?? null);
    setMergeModalOpen(true);
  };

  const handleModalClose = () => {
    setMergeModalOpen(false);
    setSelectedSourceId(null);
    setInitialTargetId(null);
    queryClient.invalidateQueries({ queryKey: ["adminMergeSuggestions"] });
  };

  return (
    <div className="px-4 py-8">
      <Link
        to="/admin"
        className="mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        &larr; Admin valdymo skydas
      </Link>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Sujungimas</h1>
        {result && (
          <p className="mt-1 text-sm text-gray-500">
            Laukia patvirtinimo: {result.totalCount}
          </p>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : result && result.games.length > 0 ? (
        <>
          <div className="space-y-4">
            {result.games.map((candidate) => (
              <CandidateCard
                key={candidate.id}
                candidate={candidate}
                onMerge={(targetId?: string) => openMergeModal(candidate.id, targetId)}
                onSkip={() => approveMutation.mutate(candidate.id)}
                isSkipping={approveMutation.isPending}
                onTitleUpdate={(title) =>
                  updateGameMutation.mutate({ id: candidate.id, title })
                }
              />
            ))}
          </div>

          <div className="mt-6">
            <Pagination
              currentPage={result.currentPage}
              totalPages={result.totalPages}
              onPageChange={setPage}
            />
          </div>
        </>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white py-12 text-center">
          <p className="text-gray-500">Nėra žaidimų laukiančių patvirtinimo</p>
        </div>
      )}

      {mergeModalOpen && sourceGame && (
        <GameMergeModal
          open={mergeModalOpen}
          onClose={handleModalClose}
          sourceGame={sourceGame}
          initialTargetId={initialTargetId}
        />
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────

function CandidateCard({
  candidate,
  onMerge,
  onSkip,
  isSkipping,
  onTitleUpdate,
}: {
  candidate: MergeCandidate;
  onMerge: (targetId?: string) => void;
  onSkip: () => void;
  isSkipping: boolean;
  onTitleUpdate: (title: string) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(candidate.title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);

  const handleTitleSave = () => {
    const trimmed = editTitle.trim();
    if (trimmed && trimmed !== candidate.title) {
      onTitleUpdate(trimmed);
    }
    setIsEditing(false);
  };

  const sortedSuggestions = [...candidate.suggestions].sort((a, b) => {
    const aBgg = a.isBggMatch || !!a.bggId;
    const bBgg = b.isBggMatch || !!b.bggId;
    if (aBgg && !bBgg) return -1;
    if (!aBgg && bBgg) return 1;
    return 0;
  });

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex gap-4">
        {/* Game info */}
        <GameThumb url={candidate.primaryImageUrl || candidate.imageUrl} alt={candidate.title} />
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-1.5">
              {isEditing ? (
                <form
                  onSubmit={(e) => { e.preventDefault(); handleTitleSave(); }}
                  className="flex items-center gap-1.5"
                >
                  <input
                    ref={inputRef}
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    onBlur={handleTitleSave}
                    className="rounded border border-gray-300 px-2 py-0.5 text-sm font-semibold text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </form>
              ) : (
                <>
                  <Link
                    to={`/admin/games/${candidate.id}`}
                    className="text-sm font-semibold text-gray-900 hover:text-primary-800"
                  >
                    {candidate.title}
                  </Link>
                  <button
                    onClick={() => { setEditTitle(candidate.title); setIsEditing(true); }}
                    className="text-gray-400 hover:text-gray-600"
                    title="Redaguoti pavadinimą"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
                    </svg>
                  </button>
                </>
              )}
              {candidate.yearPublished && (
                <span className="text-sm text-gray-400">
                  ({candidate.yearPublished})
                </span>
              )}
            </div>
          </div>
          <div className="mt-1 flex gap-3 text-xs text-gray-400">
            {candidate.dataSource && <span>{candidate.dataSource}</span>}
            <span>{candidate.activeListingsCount} skelbimai</span>
            <span>{formatDate(candidate.createdAt)}</span>
          </div>

          {/* Suggestions */}
          {sortedSuggestions.length > 0 && (
            <div className="mt-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">
                Siulomos atitiktys
              </p>
              <div className="flex flex-wrap gap-3">
                {sortedSuggestions.map((suggestion) => (
                  <SuggestionCard
                    key={suggestion.id}
                    suggestion={suggestion}
                    onSelect={() => onMerge(suggestion.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {candidate.suggestions.length === 0 && (
            <p className="mt-3 text-xs text-gray-400">
              Atitikčių nerasta
            </p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="mt-3 flex justify-end gap-2 border-t border-gray-100 pt-3">
        <button
          onClick={() => onMerge()}
          className="rounded-lg border border-primary-300 px-3 py-1.5 text-xs font-medium text-primary-800 hover:bg-primary-50"
        >
          Ieškoti kitą
        </button>
        <button
          onClick={onSkip}
          disabled={isSkipping}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        >
          Patvirtinti
        </button>
      </div>
    </div>
  );
}

function SuggestionCard({
  suggestion,
  onSelect,
}: {
  suggestion: MergeSuggestion;
  onSelect: () => void;
}) {
  const pct = Math.round(suggestion.combinedScore * 100);
  const color =
    suggestion.combinedScore >= 0.85
      ? "bg-green-100 text-green-800"
      : suggestion.combinedScore >= 0.7
        ? "bg-yellow-100 text-yellow-800"
        : "bg-red-100 text-red-800";

  return (
    <div className="flex w-48 flex-col rounded-lg border border-gray-200 bg-white p-2 shadow-sm">
      <GameThumb
        url={suggestion.primaryImageUrl || suggestion.thumbnailUrl}
        alt={suggestion.title}
        small
      />
      <div className="mt-1.5">
        <Link
          to={`/admin/games/${suggestion.id}`}
          className="line-clamp-2 text-xs font-medium text-gray-900 hover:text-primary-800"
          title={suggestion.title}
        >
          {suggestion.title}
        </Link>
        {suggestion.yearPublished && (
          <span className="text-xs text-gray-400"> ({suggestion.yearPublished})</span>
        )}
        <div className="mt-1 flex items-center gap-1">
          <span className={`inline-flex rounded-full px-1.5 py-0.5 text-xs font-medium ${color}`}>
            {pct}%
          </span>
          {suggestion.bggId && (
            <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-800">
              BGG
            </span>
          )}
        </div>
      </div>
      <button
        onClick={onSelect}
        className="mt-2 w-full rounded-md bg-primary-800 px-2 py-1 text-xs font-medium text-white hover:bg-primary-700"
      >
        Sujungti
      </button>
    </div>
  );
}

function GameThumb({
  url,
  alt,
  small,
}: {
  url: string | null;
  alt: string;
  small?: boolean;
}) {
  const size = small ? "h-24 w-24" : "h-32 w-32";
  if (url) {
    return <img src={url} alt={alt} className={`${size} shrink-0 rounded-lg object-cover`} />;
  }
  return (
    <div className={`flex ${size} shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-400`}>
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z"
        />
      </svg>
    </div>
  );
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleString("lt-LT", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}
